import { useCallback, useEffect, useMemo, useState } from 'react'
import { useExperimentService, useUserService } from '../services'
import { getSessionStorageItem, setSessionStorageItem, SessionStorageKey } from '@lib/utils/session-storage'

interface UseExitIntentModal {
  inExitIntentExperiment: boolean
  isExitModalOpen: boolean
  closeExitModal: () => void
}

export const useExitIntentModal = (): UseExitIntentModal => {
  const { currentUser } = useUserService()
  const { getToolPageExitIntentModalAssignment } = useExperimentService()

  const assignment = useMemo(
    () => getToolPageExitIntentModalAssignment(currentUser.isAnonymous),
    [currentUser.isAnonymous, getToolPageExitIntentModalAssignment]
  )

  const isTestVariant = assignment === 'test'

  const [hasShownThisSession, setHasShownThisSession] = useState(() => {
    return getSessionStorageItem(SessionStorageKey.exitIntentModalShown) === 'true'
  })

  const [isExitModalOpen, setIsExitModalOpen] = useState(false)

  useEffect(() => {
    if (!isTestVariant || hasShownThisSession || !currentUser.isAnonymous) {
      return
    }

    const handleMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 10) {
        if (!e.relatedTarget || e.relatedTarget === null) {
          setIsExitModalOpen(true)
          setSessionStorageItem(SessionStorageKey.exitIntentModalShown, 'true')
          setHasShownThisSession(true)
        }
      }
    }

    document.addEventListener('mouseout', handleMouseOut)

    return () => {
      document.removeEventListener('mouseout', handleMouseOut)
    }
  }, [isTestVariant, hasShownThisSession, currentUser.isAnonymous])

  const closeExitModal = useCallback(() => {
    setIsExitModalOpen(false)
  }, [])

  return {
    inExitIntentExperiment: isTestVariant,
    isExitModalOpen,
    closeExitModal,
  }
}
